import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Flex,
  Image,
  Link,
  Typography,
} from '@iheartjane/reefer';
import type { ChangeEvent } from 'react';
import { useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLocation, useParams } from 'react-router';

import {
  EventNames,
  FAMILIAR_MAX_IMAGE_SIZE,
  MAX_IMAGE_SIZE,
  VALID_IMAGE_EXTENSIONS,
  normalizePath,
  track,
  validateImage,
} from '@jane/business-admin/util';
import { ImagePreview } from '@jane/shared/util';

interface ImageUploadAreaProps {
  height?: number;
  image?: string;
  isMobile?: boolean;
  onImageChange: (imageUrl: string) => void;
  onImageRemove: () => void;
  onValidationError?: (message: string) => void;
  pagination?: string;
  showFallbackText?: boolean;
  subtitle?: string;
  title: string;
  useFullWidth?: boolean;
  width?: number;
}

export const ImageUploadArea = ({
  title,
  subtitle,
  image,
  onImageChange,
  onImageRemove,
  width = 400,
  height = 240,
  pagination,
  isMobile = false,
  showFallbackText = false,
  useFullWidth = false,
  onValidationError,
}: ImageUploadAreaProps) => {
  const { pathname } = useLocation();
  const { id = '' } = useParams<'id'>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!validateImage(file)) {
        // Show error for invalid images
        const errorMessage = `Invalid image type or size! Image must be of type ${VALID_IMAGE_EXTENSIONS.map(
          (ext) => `*${ext}`
        )
          .join(', ')
          .replace(
            /, ([^,]*)$/,
            ' or $1'
          )} and smaller than ${FAMILIAR_MAX_IMAGE_SIZE}.`;

        if (onValidationError) {
          onValidationError(errorMessage);
        }
        return;
      }

      try {
        const image = await ImagePreview.parse(file);
        onImageChange(image.url);

        track({
          event: EventNames.EditedPhoto,
          action: 'upload',
          file_size: `${file.size} bytes`,
          file_type: file.type,
          settings_name: 'carousel_banner_image',
          url: normalizePath(pathname, id),
        });
      } catch (error) {
        console.error('Error uploading carousel image:', error);
      }
    },
    [onValidationError, onImageChange, pathname, id]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        handleImageUpload(file);
      }
    },
    [handleImageUpload]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': VALID_IMAGE_EXTENSIONS },
    multiple: false,
    maxSize: MAX_IMAGE_SIZE,
    noClick: true,
  });

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteImage = () => {
    onImageRemove();
    // Track image deletion without file info since file is being removed
    track({
      event: EventNames.EditedPhoto,
      action: 'delete',
      file_size: '0 bytes',
      file_type: 'image/unknown',
      settings_name: 'carousel_banner_image',
      url: normalizePath(pathname, id),
    });
  };

  const handleReplaceImage = () => {
    // Clear the input value to ensure onChange fires even if the same file is selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  return (
    <Flex flexDirection="column" gap={4}>
      <Flex justifyContent="space-between">
        <Flex flexDirection="column">
          <Typography variant="body-bold">{title}</Typography>
          {subtitle && (
            <Typography variant="mini" color="grays-dark">
              {subtitle}
            </Typography>
          )}
        </Flex>
        {image && (
          <Flex gap={16}>
            <Link onClick={handleDeleteImage}>Delete image</Link>
            <Link onClick={handleReplaceImage}>Replace image</Link>
          </Flex>
        )}
      </Flex>

      <Flex
        width="100%"
        maxWidth={useFullWidth ? undefined : width}
        background="grays-white"
        p={24}
        borderRadius="lg"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap={showFallbackText && image ? 12 : 0}
      >
        <div
          style={{
            height: useFullWidth ? `${height}px` : '100%',
            width: useFullWidth ? `${width}px` : '100%',
            maxWidth: useFullWidth ? `${width}px` : '100%',
            position: 'relative',
            aspectRatio: useFullWidth ? undefined : `${width} / ${height}`,
          }}
          {...getRootProps()}
        >
          {image ? (
            <Image
              key={image}
              src={image}
              altText={`Carousel slide preview ${image}`}
              height="100%"
              objectFit="cover"
              borderRadius="rounded"
            />
          ) : (
            <Flex
              width="100%"
              height="100%"
              border="grays-light"
              borderWidth="2px"
              borderRadius="lg"
              alignItems="center"
              justifyContent="center"
              background="grays-ultralight"
              position="relative"
              style={{
                transition: 'background-color 0.2s ease',
                cursor: image ? 'default' : 'pointer',
                borderStyle: 'dashed',
              }}
            >
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={8}
              >
                <Button
                  size="default"
                  variant="secondary"
                  onClick={handleUploadClick}
                  label="Upload image"
                />
                <Typography variant="body" color="grays-dark">
                  or
                </Typography>
                <Typography variant="body" color="grays-dark">
                  Drag and drop image here
                </Typography>
              </Flex>
            </Flex>
          )}

          {pagination && image && (
            <>
              {!isMobile && (
                <>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: '#452359',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      position: 'absolute',
                      left: '10px',
                      top: 'calc(50% - 24px)',
                    }}
                  >
                    <ChevronLeftIcon
                      altText="View previous item"
                      color="grays-white"
                    />
                  </div>

                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: '#452359',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      position: 'absolute',
                      right: '10px',
                      top: 'calc(50% - 24px)',
                    }}
                  >
                    <ChevronRightIcon
                      altText="View next item"
                      color="grays-white"
                    />
                  </div>
                </>
              )}
              <div
                style={{
                  position: 'absolute',
                  bottom: isMobile ? 'auto' : 16,
                  right: 16,
                  top: isMobile ? 16 : 'auto',
                  background: 'rgb(0 0 0 / 50%)',
                  borderRadius: '16px',
                  padding: '3px 8px 5px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  height: '24px',
                }}
              >
                <Typography variant="mini" color="grays-white">
                  {pagination}
                </Typography>
              </div>
            </>
          )}
        </div>
        {showFallbackText && (
          <Typography variant="body" color="grays-mid" textAlign="center">
            Generated based on the uploaded Desktop image.
          </Typography>
        )}
        <input
          {...getInputProps()}
          ref={fileInputRef}
          type="file"
          accept={VALID_IMAGE_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Flex>
    </Flex>
  );
};
